/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useReducer } from "react";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import {
  EventsSortInput,
  EventsSortFields,
  IncomingEventsSortFields,
  IncomingEventsSortInput,
  PlanSortFields,
  SortOrder,
  useGetEventsViewQuery,
  useGetIncomingEventsQuery,
} from "~/generated/graphql";
import { Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { useDisclosure, useQueryState } from "~/hooks";
import { useGetAllActions } from "~/hooks/useGetAllActions";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { eventsExcludeFilterMap } from "~/tools/filterStateMaps";
import {
  eventLogOutputCheckboxes,
  eventStatusCheckboxes,
  incomingEventStatusCheckboxes,
  QueryGetEvents,
} from "~/views/events/components/constants";
import { useGetPlans } from "~/views/plans/hooks";
import { EventType } from "../types";
import {
  prepareEventsFilterBy,
  prepareIncomingEventsFilterBy,
} from "./eventFilterBuilders";

type State = {
  eventStatuses: string[];
  excludeEventStatuses: boolean;
  eventRelatedPlanIds: string[];
  excludeEventRelatedPlanIds: boolean;
  eventActionIds: string[];
  excludeEventActionIds: boolean;
  eventLogOutputs: string[];
  excludeEventLogOutputs: boolean;
};
type QueryGetEventsState = QueryGetEvents & {
  page?: string | number;
  limit?: string | number;
};

function buildState(queryState: QueryGetEvents) {
  return {
    eventStatuses: queryState.eventStatuses,
    excludeEventStatuses: queryState.excludeEventStatuses,
    eventActionIds: queryState.eventActionIds,
    excludeEventActionIds: queryState.excludeEventActionIds,
    eventRelatedPlanIds: queryState.eventRelatedPlanIds,
    excludeEventRelatedPlanIds: queryState.excludeEventRelatedPlanIds,
    eventLogOutputs: queryState.eventLogOutputs,
    excludeEventLogOutputs: queryState.excludeEventLogOutputs,
  };
}

function buildQueryStateFromSidebarState(
  queryState: QueryGetEventsState,
  state: State
): QueryGetEventsState {
  return {
    ...queryState,
    eventStatuses: state.eventStatuses,
    excludeEventStatuses: state.excludeEventStatuses,
    eventActionIds: state.eventActionIds,
    excludeEventActionIds: state.excludeEventActionIds,
    eventRelatedPlanIds: state.eventRelatedPlanIds,
    excludeEventRelatedPlanIds: state.excludeEventRelatedPlanIds,
    eventLogOutputs: state.eventLogOutputs,
    excludeEventLogOutputs: state.excludeEventLogOutputs,
  };
}

// eslint-disable-next-line complexity
export function EventFiltersSidebar({
  resultsCount,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<QueryGetEvents>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    eventsExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );
  const phrase = isNotEmpty(draftQueryState.searchPhrase?.trim() || "")
    ? draftQueryState.searchPhrase
    : undefined;

  const eventsSort: EventsSortInput = {
    field:
      (draftQueryState.sortField as EventsSortFields) ??
      EventsSortFields.StartTime,
    order: draftQueryState.order ?? SortOrder.Desc,
  };
  const incomingSort: IncomingEventsSortInput = {
    field:
      (draftQueryState.sortField as IncomingEventsSortFields) ??
      IncomingEventsSortFields.StartTime,
    order: draftQueryState.order ?? SortOrder.Desc,
  };

  const { data: eventsPreviewData, loading: eventsPreviewLoading } =
    useGetEventsViewQuery({
      variables: {
        page: Number(draftQueryState.page ?? "1"),
        limit: Number(draftQueryState.limit ?? "20"),
        sort: eventsSort,
        filter: {
          filterBy: prepareEventsFilterBy(draftQueryState),
          phrase,
        },
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "network-only",
      skip:
        !isOpen ||
        !hasPendingChanges ||
        draftQueryState.eventsType === EventType.future,
    });
  const { data: incomingPreviewData, loading: incomingPreviewLoading } =
    useGetIncomingEventsQuery({
      variables: {
        page: Number(draftQueryState.page ?? "1"),
        limit: Number(draftQueryState.limit ?? "20"),
        sort: incomingSort,
        filter: {
          filterBy: prepareIncomingEventsFilterBy(draftQueryState),
          phrase,
        },
      },
      skip:
        !isOpen ||
        !hasPendingChanges ||
        draftQueryState.eventsType !== EventType.future,
    });

  const previewResultsCount =
    draftQueryState.eventsType === EventType.future
      ? incomingPreviewData?.incomingEvents?.pageInfo?.count
      : eventsPreviewData?.eventsView?.pageInfo?.count;
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading:
      draftQueryState.eventsType === EventType.future
        ? incomingPreviewLoading
        : eventsPreviewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "events",
    defaultLabelWhenNoAppliedCount: true,
  });

  const clearEventStatusFilters = () => {
    updateState({ eventStatuses: [], excludeEventStatuses: false });
  };

  const clearEventActionsFilter = () => {
    updateState({ eventActionIds: [], excludeEventActionIds: false });
  };

  const clearEventRelatedPlanIdsFilter = () => {
    updateState({ eventRelatedPlanIds: [], excludeEventRelatedPlanIds: false });
  };

  const clearEventLogOutputsFilter = () => {
    updateState({ eventLogOutputs: [], excludeEventLogOutputs: false });
  };

  const clearAllFilters = () => {
    updateState({
      eventStatuses: [],
      excludeEventStatuses: false,
      eventActionIds: [],
      excludeEventActionIds: false,
      eventRelatedPlanIds: [],
      excludeEventRelatedPlanIds: false,
      eventLogOutputs: [],
      excludeEventLogOutputs: false,
    });

    updateQueryState({
      ...queryState,
      eventStatuses: [],
      excludeEventStatuses: false,
      eventActionIds: [],
      excludeEventActionIds: false,
      eventRelatedPlanIds: [],
      excludeEventRelatedPlanIds: false,
      eventLogOutputs: [],
      excludeEventLogOutputs: false,
      page: "1",
    });
  };

  const applyFilters = () => {
    updateQueryState({
      ...queryState,
      eventStatuses: state.eventStatuses,
      excludeEventStatuses: state.excludeEventStatuses,
      eventActionIds: state.eventActionIds,
      excludeEventActionIds: state.excludeEventActionIds,
      eventRelatedPlanIds: state.eventRelatedPlanIds,
      excludeEventRelatedPlanIds: state.excludeEventRelatedPlanIds,
      eventLogOutputs: state.eventLogOutputs,
      excludeEventLogOutputs: state.excludeEventLogOutputs,
      page: "1",
    });

    close();
  };

  const baseFilterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "events",
    excludeFilterMap: eventsExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? baseFilterSidebarState.helperText;
  const filterSidebarState = {
    ...baseFilterSidebarState,
    helperText,
  };

  const { data } = useGetPlans({
    page: "1",
    limit: "1000",
    sort: {
      field: PlanSortFields.Name,
      order: SortOrder.Asc,
    },
  });

  const planCheckboxes = data.map((plan) => ({
    value: plan.id,
    label: plan.name,
  }));

  const { customActions, systemActions } = useGetAllActions();

  const eventActionCheckboxes = [
    ...systemActions.map((action) => ({
      value: action.id,
      label: action.name,
    })),
    ...customActions.map((action) => ({
      value: action.id,
      label: action.name,
      subLabel: "Custom Action",
    })),
  ];

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={filterSidebarState.helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Event Status"
            isSelected={
              isNotNil(state.eventStatuses) && isNotEmpty(state.eventStatuses)
            }
            selectedOptions={state.eventStatuses ?? []}
            onClear={clearEventStatusFilters}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.eventStatuses ?? []}
              selectableItems={
                queryState.eventsType === EventType.pastCurrent
                  ? eventStatusCheckboxes
                  : incomingEventStatusCheckboxes
              }
              isExcludeSelected={state.excludeEventStatuses}
              onMultiSelect={(statuses) =>
                updateState({ eventStatuses: statuses })
              }
              onModeChange={(mode) =>
                updateState({ excludeEventStatuses: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Event Actions"
            isSelected={
              isNotNil(state.eventActionIds) && isNotEmpty(state.eventActionIds)
            }
            selectedOptions={state.eventActionIds ?? []}
            onClear={clearEventActionsFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.eventActionIds ?? []}
              selectableItems={eventActionCheckboxes}
              isExcludeSelected={state.excludeEventActionIds}
              onMultiSelect={(actionIds) =>
                updateState({ eventActionIds: actionIds })
              }
              onModeChange={(mode) =>
                updateState({ excludeEventActionIds: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Event Related Plan"
            isSelected={
              isNotNil(state.eventRelatedPlanIds) &&
              isNotEmpty(state.eventRelatedPlanIds)
            }
            selectedOptions={state.eventRelatedPlanIds ?? []}
            onClear={clearEventRelatedPlanIdsFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.eventRelatedPlanIds ?? []}
              selectableItems={planCheckboxes}
              isExcludeSelected={state.excludeEventRelatedPlanIds}
              onMultiSelect={(planIds) =>
                updateState({ eventRelatedPlanIds: planIds })
              }
              onModeChange={(mode) =>
                updateState({
                  excludeEventRelatedPlanIds: mode === Mode.EXCLUDES,
                })
              }
            />
          </FilterWrapper>
          <When condition={queryState.eventsType === EventType.pastCurrent}>
            <FilterWrapper
              name="Log Output"
              isSelected={
                isNotNil(state.eventLogOutputs) &&
                isNotEmpty(state.eventLogOutputs)
              }
              selectedOptions={state.eventLogOutputs ?? []}
              onClear={clearEventLogOutputsFilter}
            >
              <SelectableListWithSearch
                allowModeChange
                selectedOptions={state.eventLogOutputs ?? []}
                selectableItems={eventLogOutputCheckboxes}
                isExcludeSelected={state.excludeEventLogOutputs}
                onMultiSelect={(eventLogOutputs) =>
                  updateState({ eventLogOutputs })
                }
                onModeChange={(mode) =>
                  updateState({
                    excludeEventLogOutputs: mode === Mode.EXCLUDES,
                  })
                }
              />
            </FilterWrapper>
          </When>
        </Sidebar>
      </When>
    </>
  );
}
